import type { SanityPageData } from 'types';
import type { StaticSiteNavClickProperties, StaticSiteNavOpenedProperties } from 'utils/metrics';

export type NavRegion = 'footer' | 'main_nav';

export type NavSurface = 'desktop_horizontal' | 'footer' | 'mobile_menu';

export type NavItemIdentity = {
  alt?: string;
  depth?: number;
  elementType?: 'callout_image' | 'link' | 'social_icon';
  footerGroup?: string;
  index: number;
  key: string;
  title?: string;
};

export type NavPlacement = {
  chain: NavItemIdentity[];
  region: NavRegion;
  surface: NavSurface;
};

export type NavPage = {
  host: string;
  path: string;
  sanityPage: SanityPageData | null;
};

type NavClick = {
  href: string;
  text?: string;
};

// Reported as item_key, so renaming one splits its click history
export const builtInLinks = {
  contactUs: { footerGroup: 'help', index: 0, key: 'footer-contact-us', title: 'Contact Us' },
  emailUs: { footerGroup: 'help', index: 3, key: 'footer-email-us', title: 'Email Us' },
  faq: { footerGroup: 'help', index: 1, key: 'footer-faq', title: 'Frequently Asked Questions' },
  help: { index: 4, key: 'help', title: 'Help' },
  login: { index: 2, key: 'login', title: 'Login' },
  logo: { index: 0, key: 'logo', title: 'Logo' },
  mobileMenu: { depth: 0, index: 0, key: 'mobile-menu', title: 'Menu' },
  phone: { footerGroup: 'help', index: 2, key: 'footer-phone' },
  sitemap: { footerGroup: 'legal', index: 0, key: 'footer-sitemap', title: 'Sitemap' },
  smallScreenCollection: { index: 1, key: 'small-screen-collection', title: 'Collection' },
  startYourEvent: { index: 3, key: 'start-your-event', title: 'Start Your Event' },
};

export const breadcrumbLink = (title: string, index: number): NavItemIdentity => ({
  footerGroup: 'breadcrumbs',
  index,
  key: 'footer-breadcrumb',
  title,
});

export const navItemLink = (item: { _key: string; title: string }, siblings: Array<{ _key: string }>): NavItemIdentity => ({
  index: siblings.findIndex(({ _key }) => _key === item._key),
  key: item._key,
  title: item.title,
});

export const socialIconLink = (key: string, site: string, index: number): NavItemIdentity => ({
  elementType: 'social_icon',
  footerGroup: 'follow_us',
  index,
  key,
  title: site,
});

const nameOf = ({ alt, key, title }: NavItemIdentity, text?: string) => title || text?.trim() || alt || key;

const isExternalHref = (href: string, host: string) => {
  try {
    return new URL(href, `https://${host}`).host !== host;
  } catch {
    return false;
  }
};

const toPageProperties = ({ path, sanityPage }: NavPage) =>
  sanityPage
    ? { page_slug: sanityPage.slug, page_id: sanityPage.id, page_name: sanityPage.title }
    : { page_slug: path.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '') };

const toItemProperties = ({ chain, region, surface }: NavPlacement, page: NavPage, text?: string) => {
  const item = chain[chain.length - 1];

  return {
    nav_region: region,
    nav_surface: surface,
    item_key: item.key,
    item_name: nameOf(item, text),
    nav_depth: item.depth ?? chain.length,
    ...toPageProperties(page),
  };
};

export const toNavOpenedProperties = (placement: NavPlacement, page: NavPage): StaticSiteNavOpenedProperties =>
  toItemProperties(placement, page);

export const toNavClickProperties = (placement: NavPlacement, page: NavPage, { href, text }: NavClick): StaticSiteNavClickProperties => {
  const ancestors = placement.chain.slice(0, -1);
  const item = placement.chain[placement.chain.length - 1];
  const parent = ancestors[ancestors.length - 1];
  const properties = toItemProperties(placement, page, text);

  return {
    ...properties,
    item_href: href,
    nav_path: [...ancestors.map((ancestor) => nameOf(ancestor)), properties.item_name].join(' / '),
    ...(parent && { parent_key: parent.key }),
    item_index: item.index + 1,
    element_type: item.elementType ?? 'link',
    is_external: isExternalHref(href, page.host),
    ...(item.footerGroup && { footer_group: item.footerGroup }),
  };
};
